
import Page from '@/Page.vue';
import { Component, Prop, PropSync, Ref, VModel, Watch } from 'vue-property-decorator';
import { findingsStore } from '@/libs/findings/+state/store';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import {
    CategoryModel,
    FindingModel,
    ImplementationStateEnum,
    MeasureModel,
    MeasureTypeEnum,
    PlanItemModel,
    ProcessTypeEnum,
    EscalationGroupTypeEnum,
    QueryOperator,
    ResponseModel,
    TenantUserModel,
} from '@/libs/Api';
import WorkTaskFindingItemMeasure from '../components/WorkTaskFindingItemMeasure.vue';
import { cloneDeep } from 'lodash';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import WorkTaskDialogFiles from '../components/WorkTaskDialogFiles.vue';
import moment from 'moment';
import WorkTaskEndShutdown from '@/libs/work-tasks/components/WorkTaskEndShutdown.vue';
import { measuresStore } from '@/libs/measures/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { rules } from '@typescript-eslint/eslint-plugin';
import WorkTaskEscalation from '@/libs/work-tasks/components/WorkTaskEscalation.vue';

@Component({
    components: {
        WorkTaskEscalation,
        WorkTaskEndShutdown,
        WorkTaskFindingItemMeasure,
        WorkTaskDialogFiles,
    },
})
export default class WorkTaskFindingItem extends Page {
    /* Component props */
    @VModel() dialog!: boolean;
    @Prop() planItem!: PlanItemModel;
    @Prop() category!: CategoryModel;

    /* Component state variables */
    valid = true;
    saving = false;
    hasChanges = true;
    dialogValid = true;
    endShutdownDialog = false;
    isEndShutdownMandatory = false;
    dialogModel: FindingModel | null = null;
    basicRules = [(v: any) => !!v || this.$t('validation.thisFieldIsRequired')];

    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected EscalationGroupTypeEnum: typeof EscalationGroupTypeEnum = EscalationGroupTypeEnum;

    /* Auth variables */
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;

    /* Ref */
    @Ref() dialogForm!: any;
    @Ref() measureItems!: WorkTaskFindingItemMeasure[];

    @Watch('dialog')
    dialogChange(v: boolean, p: boolean) {
        if (v && v != p) {
            const responses = this.planItem?.responses ?? [];
            this.dialogModel = {
                description: '',
                measures: [],
                findingFiles: [],
                closeReason: null,
                findingManagers: [
                    {
                        tenantUserId: this.tenantUser.id,
                    },
                ],
                useDefinedManagers: true,
                cause: '',
                isClosed: false,
                responseId: responses.length == 1 ? responses[0].id : undefined,
            };
            this.addMeasure();
        }
    }

    get responses(): ResponseModel[] {
        return this.planItem?.responses ?? [];
    }

    closeDialog() {
        this.$emit('input', false);
    }

    save() {
        if (this.dialogForm.validate() && this.dialogModel) {
            this.saving = true;
            const dialogModel = cloneDeep(this.dialogModel)!;
            dialogModel.measures = this.measureItems?.map((e) => e.getMeasure()!) ?? [];
            dialogModel!.measures!.forEach((x) => {
                x.sequenceIndex = dialogModel!.measures!.indexOf(x) + 1;
            });
            if (this.category.processType != ProcessTypeEnum.IssueSolving) {
                dialogModel?.measures?.forEach((e) => {
                    if (e.implementerUserId) {
                        if (
                            e.measureImplementationUsers!.length == 0 ||
                            e.measureImplementationUsers![0].tenantUserId != e.implementerUserId
                        ) {
                            e.measureImplementationUsers = [
                                {
                                    tenantUserId: e.implementerUserId,
                                },
                            ];
                        }
                    }
                });
            }
            if (dialogModel.isClosed) {
                dialogModel.closedById = this.tenantUser.id;
                dialogModel.closedAt = moment().format('YYYY-MM-DDTHH:mm:ss');
                dialogModel.measures = [];
            } else {
                dialogModel.closedById = null;
                dialogModel.closedAt = null;
            }
            findingsStore.dispatch(CrudAction.Create, { item: dialogModel });
            this.subscribe(findingsStore, CrudReponse.Create)
                .then(() => {
                    if (this.planItem.isWorkplaceShutdown && this.planItem.shutdownToDate == null) {
                        measuresStore.dispatch(
                            CrudAction.GetAll,
                            createCrudQueryPayload<MeasureModel>(
                                undefined,
                                [
                                    {
                                        field: 'finding.response.planItemId',
                                        op: QueryOperator.Eq,
                                        comparand: this.planItem.id!,
                                    },
                                    {
                                        field: 'implementationState',
                                        op: QueryOperator.In,
                                        comparand: [ImplementationStateEnum.New, ImplementationStateEnum.InProgress],
                                    },
                                ],
                                undefined,
                                false,
                                'NotImplementedMeasures'
                            )
                        );
                        this.subscribe(measuresStore, CrudReponse.GetAll, 'NotImplementedMeasures').then(
                            (e: MeasureModel[]) => {
                                this.isEndShutdownMandatory = e.length == 0;
                                this.endShutdownDialog = true;
                                this.saving = false;
                                this.$emit('planItemChanged');
                                this.closeDialog();
                            }
                        );
                    } else {
                        this.saving = false;
                        this.$emit('planItemChanged');
                        this.closeDialog();
                    }
                })
                .catch((e) => {
                    this.saving = false;
                    console.log(e);
                });
        }
    }

    addMeasure() {
        const measure = {
            implementationState: ImplementationStateEnum.New,
            measureType: this.category.useMeasureType ? MeasureTypeEnum.Corrective : null,
            implementationDescription: null,
            measureFiles: [],
            measureImplementationUsers: [],
            immediateImplementation: false,
            immediateVerification: false,
        } as MeasureModel;
        if (
            this.category.processType == ProcessTypeEnum.Maintenance ||
            this.category.processType == ProcessTypeEnum.Checklist ||
            this.category.processType == ProcessTypeEnum.ProductInspection ||
            this.category.processType == ProcessTypeEnum.IssueSolving
        ) {
            measure.implementUntil = moment().format('YYYY-MM-DDT23:59:59');
        }
        this.dialogModel?.measures?.push(measure);
    }
}
